import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  DollarSign,
  Eye,
  FileSpreadsheet,
  Search,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import React from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { formatDate, formatPrice } from "../../utils/format";

interface CustomerStats {
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: string | null;
}

interface OrderSummary {
  createdAt: string;
}

interface Customer {
  id: number;
  email: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  stats: CustomerStats;
  orders?: OrderSummary[];
}

interface PaginationInfo {
  total: number;
  pages: number;
  currentPage: number;
  perPage: number;
}

export function CustomersPage() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);

  // Helpers for date filter
  const formatISODate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Normalize search by removing diacritics so server receives nondiacritic text
  const removeDiacritics = (value: string) =>
    value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const beforeYesterday = new Date(today);
  beforeYesterday.setDate(today.getDate() - 2);

  const beforeYesterdayISO = formatISODate(beforeYesterday);

  const pastDays = React.useMemo(() => {
    const list: { iso: string; label: string }[] = [];
    const start = new Date(beforeYesterday);
    let cursor = start;
    while (true) {
      list.push({
        iso: formatISODate(cursor),
        label: String(cursor.getDate()),
      });
      if (cursor.getDate() === 1) break;
      const prev = new Date(cursor);
      prev.setDate(cursor.getDate() - 1);
      cursor = prev;
    }
    return list;
  }, [beforeYesterdayISO]);

  const selectedISODate = React.useMemo(() => {
    if (!selectedDate) return null;
    if (selectedDate === "today") return formatISODate(today);
    if (selectedDate === "yesterday") return formatISODate(yesterday);
    return selectedDate;
  }, [selectedDate, today, yesterday]);

  // Fetch customers with pagination
  const normalizedSearch = React.useMemo(
    () => removeDiacritics(search).trim(),
    [search]
  );

  const { data, isLoading } = useQuery({
    queryKey: ["customers", page, normalizedSearch, selectedDate],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: "10",
        ...(normalizedSearch && { search: normalizedSearch }),
        ...(selectedDate && { date: selectedDate }),
      });

      const response = await fetchWithAuth(`/api/admin/customers?${params}`);
      if (!response.ok) throw new Error("Eroare la încărcarea clienților");
      return response.json() as Promise<{
        status: string;
        date: {
          customers: Customer[];
          pagination: PaginationInfo;
        };
      }>;
    },
  });

  console.log("data", data);

  // Fetch customer statistics
  const { data: stats } = useQuery({
    queryKey: ["customer-stats"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/customers/stats");
      if (!response.ok) throw new Error("Eroare la încărcarea statisticilor");
      return response.json() as Promise<{
        totalCustomers: number;
        activeCustomers: number;
        newThisMonth: number;
        averageOrderValue: number;
      }>;
    },
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="p-6 space-y-8 bg-background min-h-screen">
      <Card className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Users className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Clienți</h1>
          </div>
        </div>
        <Button
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          onClick={async () => {
            try {
              const response = await fetchWithAuth(
                "/api/admin/customers/export",
                {
                  method: "GET",
                  headers: {
                    Accept:
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  },
                }
              );
              if (!response.ok) {
                throw new Error("Eroare la exportul clienților");
              }
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `clienti-${formatDate(new Date())}.xlsx`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Export Excel
        </Button>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Users className="w-4 h-4" />
              Total Clienți
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.totalCustomers || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <UserCheck className="w-4 h-4" />
              Clienți Activi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.activeCustomers || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <UserPlus className="w-4 h-4" />
              Clienți Noi Luna Aceasta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {stats?.newThisMonth || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <DollarSign className="w-4 h-4" />
              Valoare Medie Comandă
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">
              {formatPrice(stats?.averageOrderValue || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Caută după nume, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground mr-2">
            Filtrează după zi:
          </span>
          <Button
            variant={selectedDate === "today" ? "default" : "outline"}
            onClick={() => {
              setSelectedDate("today");
              setPage(1);
            }}
            className="px-3 py-1"
          >
            Azi
          </Button>
          <Button
            variant={selectedDate === "yesterday" ? "default" : "outline"}
            onClick={() => {
              setSelectedDate("yesterday");
              setPage(1);
            }}
            className="px-3 py-1"
          >
            Ieri
          </Button>
          <Button
            variant={
              selectedDate === beforeYesterdayISO ? "default" : "outline"
            }
            onClick={() => {
              setSelectedDate(beforeYesterdayISO);
              setPage(1);
            }}
            className="px-3 py-1"
          >
            Alaltaieri
          </Button>
          {pastDays.map((d) => (
            <Button
              key={d.iso}
              variant={selectedDate === d.iso ? "default" : "outline"}
              onClick={() => {
                setSelectedDate(d.iso);
                setPage(1);
              }}
              className="w-9 h-9 p-0 flex items-center justify-center"
              title={d.iso}
            >
              {d.label}
            </Button>
          ))}
        </div>
      </Card>

      {/* Customers Table */}
      <Card className=" overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Client</TableHead>
                <TableHead className="font-semibold">
                  Data Înregistrării
                </TableHead>
                <TableHead className="font-semibold">Total Comenzi</TableHead>
                <TableHead className="font-semibold">
                  Comenzi în ziua selectată
                </TableHead>
                <TableHead className="font-semibold">Total Cheltuit</TableHead>
                <TableHead className="font-semibold">Ultima Comandă</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="flex items-center justify-center text-muted-foreground">
                      Se încarcă...
                    </div>
                  </TableCell>
                </TableRow>
              ) : data?.date.customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="text-muted-foreground">
                      Nu există clienți
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.date.customers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-muted">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">
                          {customer.name || "N/A"}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {customer.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(customer.createdAt)}</TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-800 ring-1 ring-blue-200">
                        {customer.stats.totalOrders} comenzi
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {selectedISODate ? (
                        <Badge className="bg-purple-100 text-purple-800 ring-1 ring-purple-200">
                          {customer.orders?.filter(
                            (o) =>
                              (o.createdAt || "").slice(0, 10) ===
                              selectedISODate
                          ).length || 0}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(customer.stats.totalSpent)}
                    </TableCell>
                    <TableCell>
                      {customer.stats.lastOrderDate
                        ? formatDate(customer.stats.lastOrderDate)
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-900"
                        asChild
                      >
                        <a href={`/customers/${customer.id}`}>
                          <Eye className="w-4 h-4" />
                          Detalii
                          <ChevronRight className="w-4 h-4" />
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {data?.date.pagination && data.date.pagination.total > 10 && (
          <div className="flex justify-center mt-6">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-4"
              >
                Anterior
              </Button>
              <span className="text-sm text-muted-foreground">
                Pagina {page} din {data.date.pagination.pages}
              </span>
              <Button
                variant="outline"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= data.date.pagination.pages}
                className="px-4"
              >
                Următor
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
